/**
 * AdaptiveSuggestionBanner — Renders inline adaptive training suggestions.
 *
 * Displays at most 2 contextual suggestions based on today's signals.
 * Non-intrusive: small banners with dismiss. No blocking modals.
 *
 * @since Adaptive Suggestion Layer v1
 */

import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { AdaptiveSuggestion } from "@/lib/adaptiveSuggestionEngine";

interface AdaptiveSuggestionBannerProps {
  suggestions: AdaptiveSuggestion[];
  onDismiss: (id: string) => void;
}

const SEVERITY_STYLES: Record<string, string> = {
  warning: "border-destructive/30 bg-destructive/5",
  caution: "border-yellow-500/30 bg-yellow-500/5",
  info: "border-primary/20 bg-primary/5",
};

export function AdaptiveSuggestionBanner({ suggestions, onDismiss }: AdaptiveSuggestionBannerProps) {
  const navigate = useNavigate();

  if (suggestions.length === 0) return null;

  return (
    <div className="space-y-2">
      <AnimatePresence mode="popLayout">
        {suggestions.map((s) => (
          <motion.div
            key={s.id}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8, transition: { duration: 0.15 } }}
            className={cn(
              "rounded-lg border p-3 flex items-start gap-3",
              SEVERITY_STYLES[s.severity] ?? SEVERITY_STYLES.info
            )}
          >
            <span className="text-lg flex-shrink-0 mt-0.5" role="img">
              {s.emoji}
            </span>

            <div className="flex-1 min-w-0 space-y-1.5">
              <p className="text-xs text-muted-foreground leading-relaxed">
                {s.message}
              </p>
              {s.actionLabel && s.actionRoute && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1 text-xs text-primary hover:text-primary px-2"
                  onClick={() => {
                    navigate(s.actionRoute!);
                    onDismiss(s.id);
                  }}
                >
                  {s.actionLabel}
                  <ArrowRight className="h-3 w-3" />
                </Button>
              )}
            </div>

            <button
              onClick={() => onDismiss(s.id)}
              className="flex-shrink-0 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
              aria-label="Ignorer"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
